import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../api";
import { Button } from "./ui/button";
import {
  MessagesSquare, Languages, Mic, Camera, Briefcase, Bell, ChevronRight, ChevronLeft,
} from "lucide-react";

const TOUR_KEY_PREFIX = "osc_tour_done_v1_";

export const showWelcomeTour = () => {
  window.dispatchEvent(new CustomEvent("osc-show-tour"));
};

export const WelcomeTour = () => {
  const { user, setUser, t } = useApp();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const touchX = useRef(null);

  const storageKey = user ? `${TOUR_KEY_PREFIX}${user.id}` : null;

  // Auto-open on first login OR when the Master re-enabled the tour for this
  // account (server flag). AUTHORITATIVE KILL SWITCH: when the Master left the
  // tour DISABLED (show_tour === false on the server), it must NEVER auto-open
  // — not even on a brand-new device/browser where localStorage is empty.
  useEffect(() => {
    if (!storageKey) return;
    if (user?.show_tour === false) return; // Master disabled / already viewed — never auto-show
    if (user?.show_tour || !localStorage.getItem(storageKey)) {
      const timer = setTimeout(() => { setStep(0); setOpen(true); }, 600);
      return () => clearTimeout(timer);
    }
  }, [storageKey, user?.show_tour]);

  // Manual replay (from More page)
  useEffect(() => {
    const show = () => { setStep(0); setOpen(true); };
    window.addEventListener("osc-show-tour", show);
    return () => window.removeEventListener("osc-show-tour", show);
  }, []);

  const slides = useMemo(() => [
    {
      key: "welcome",
      icons: null,
      logo: true,
      title: t("tour_welcome_title"),
      text: t("tour_welcome_text"),
    },
    {
      key: "chat",
      img: "/tour/slide-chat.jpg",
      icons: [MessagesSquare, Languages],
      title: t("tour_chat_title"),
      text: t("tour_chat_text"),
    },
    {
      key: "media",
      img: "/tour/slide-media.jpg",
      icons: [Mic, Camera],
      title: t("tour_media_title"),
      text: t("tour_media_text"),
    },
    {
      key: "jobs",
      img: "/tour/slide-jobs.jpg",
      icons: [Briefcase],
      title: t("tour_jobs_title"),
      text: t("tour_jobs_text"),
    },
    {
      key: "notifications",
      icons: [Bell],
      title: t("tour_notif_title"),
      text: t("tour_notif_text"),
    },
  ], [t]);

  const finish = () => {
    if (storageKey) localStorage.setItem(storageKey, "1");
    setOpen(false);
    // Persist server-side (show_tour: false) so the tour stays OFF on ANY
    // device — this also clears the Master's re-show flag.
    if (user && user.show_tour !== false) {
      api.patch(`/users/${user.id}`, { show_tour: false }).catch(() => {});
      setUser({ ...user, show_tour: false });
    }
  };

  const next = () => {
    if (step < slides.length - 1) setStep((s) => s + 1);
    else finish();
  };
  const prev = () => { if (step > 0) setStep((s) => s - 1); };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx < -50) next();
    else if (dx > 50) prev();
  };

  if (!open || !user) return null;

  const slide = slides[step];
  const isLast = step === slides.length - 1;

  return (
    <div
      data-testid="welcome-tour-overlay"
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/45 backdrop-blur-sm px-5"
      style={{
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="w-full max-w-sm bg-card border rounded-2xl shadow-xl overflow-hidden flex flex-col" data-testid="welcome-tour-card">
        {/* Illustration area — real app screenshots (fixed height keeps the card stable) */}
        <div className="bg-[#2B3990]/[0.06] border-b flex items-center justify-center h-56 overflow-hidden">
          {slide.logo ? (
            <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-20 w-20 rounded-2xl object-contain drop-shadow-sm" />
          ) : slide.img ? (
            <img
              src={slide.img}
              alt={slide.title}
              data-testid={`tour-slide-img-${slide.key}`}
              className="h-48 w-auto rounded-xl border shadow-md object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex items-center gap-3">
              {slide.icons.map((Icon, i) => (
                <div key={i} className="h-16 w-16 rounded-2xl bg-card border shadow-sm flex items-center justify-center">
                  <Icon className="h-8 w-8 text-[#2B3990]" strokeWidth={1.8} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Text */}
        <div className="px-6 pt-5 pb-2 text-center min-h-[120px]">
          <h2 className="font-display font-bold text-lg" data-testid="tour-slide-title">{slide.title}</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="tour-slide-text">{slide.text}</p>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 py-3" data-testid="tour-dots">
          {slides.map((s, i) => (
            <button
              key={s.key}
              aria-label={`slide ${i + 1}`}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${
                i === step ? "w-5 bg-[#2B3990]" : "w-1.5 bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 pt-1 space-y-2">
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                data-testid="tour-prev-btn"
                className="h-11 px-4"
                onClick={prev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <Button
              data-testid="tour-next-btn"
              className="h-11 flex-1 bg-[#2B3990] hover:bg-[#232e75] text-white"
              onClick={next}
            >
              {isLast ? t("tour_start") : t("tour_next")}
              {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
          {!isLast && (
            <button
              type="button"
              data-testid="tour-skip-btn"
              onClick={finish}
              className="w-full text-center text-xs font-medium text-muted-foreground hover:text-foreground py-1"
            >
              {t("tour_skip")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
